/**
 * Phase 66 Plan 02 — JWT payload decoder (no signature verification).
 *
 * Single source of truth, replacing two parallel implementations:
 *   - js/app.js `decodeJwtRole` (lines 88-103) — role-only
 *   - js/pages/admin-pg-export.js `decodeJwtPayload` (lines 49-58) — full claims
 *
 * SECURITY: this function does NOT verify the JWT signature. The backend
 * is the only authoritative validator. We extract claims here purely for
 * UI gating (sidebar visibility, RBAC <Can>, tenant context). Tampered
 * tokens fail at the next API call (backend returns 401 → AuthProvider
 * triggers logout via authBus).
 *
 * @see PATTERNS.md §i18n + utilities — `src/lib/jwt.ts`
 */

export type JwtPayload = {
  role?: string;
  role_scopes?: string[];
  circuit_id?: string | null;
  sub?: string;
  exp?: number;
  iat?: number;
  // Other claims survive but aren't typed here.
  [key: string]: unknown;
};

/**
 * Decode the payload section of a JWT (the middle base64url segment).
 * Returns null on any parse / shape error — never throws.
 *
 * Implementation port of js/app.js `decodeJwtRole` extended to return
 * the full payload object. Handles base64url → base64 conversion
 * (replace -_ with +/) and padding restoration verbatim.
 */
export function decodeJwtPayload(token: string | null | undefined): JwtPayload | null {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = b64.length % 4;
    if (pad === 2) b64 += "==";
    else if (pad === 3) b64 += "=";
    else if (pad !== 0) return null;
    const json = atob(b64);
    const payload = JSON.parse(json) as unknown;
    if (!payload || typeof payload !== "object") return null;
    return payload as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * Extract the `role` claim only (back-compat shim for Phase 65 callers
 * that don't need the full payload). Returns null on any error.
 */
export function decodeJwtRole(token: string | null | undefined): string | null {
  const payload = decodeJwtPayload(token);
  return payload && typeof payload.role === "string" ? payload.role : null;
}
